import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Radar, Search, MapPin, PackageCheck, Truck, Plane, ShieldCheck, Lock, AlertCircle,
  Loader2, CircleDot, Anchor, Ship, PauseCircle, RotateCcw
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";

const STATUS_ICONS = {
  "Pending Pickup": PackageCheck,
  "Package Collected": PackageCheck,
  "In Transit": Truck,
  "Border Crossing": Plane,
  "Held by Customs": PauseCircle,
  "Released by Customs": ShieldCheck,
  "Customs Clearance": ShieldCheck,
  "Out for Delivery": Truck,
  "Delivered": PackageCheck,
  "Vaulted": Lock,
  "On Hold": AlertCircle,
  "Delayed": AlertCircle,
  "Damaged": AlertCircle,
  "Returned to Sender": RotateCcw,
};

// Approximate {lon,lat} for common country names
const COUNTRY_COORDS = {
  "united states": [-98, 39], "usa": [-98, 39], "us": [-98, 39],
  "canada": [-96, 56], "mexico": [-102, 24],
  "brazil": [-51, -14], "argentina": [-64, -38],
  "united kingdom": [-2, 54], "uk": [-2, 54], "england": [-1, 52],
  "france": [2, 46], "germany": [10, 51], "switzerland": [8, 47],
  "italy": [12, 43], "spain": [-4, 40], "portugal": [-8, 39],
  "netherlands": [5, 52], "belgium": [4, 50], "sweden": [15, 60], "norway": [8, 60],
  "russia": [90, 60], "poland": [19, 52],
  "china": [104, 35], "japan": [138, 36], "south korea": [128, 36], "korea": [128, 36],
  "india": [78, 20], "pakistan": [69, 30], "bangladesh": [90, 24],
  "singapore": [103.8, 1.35], "malaysia": [102, 4], "thailand": [100, 15], "vietnam": [108, 16],
  "indonesia": [113, -0.7], "philippines": [122, 12],
  "hong kong": [114.2, 22.3], "taiwan": [121, 24],
  "uae": [54, 24], "united arab emirates": [54, 24], "saudi arabia": [45, 24],
  "qatar": [51, 25], "kuwait": [47, 29], "israel": [35, 31], "turkey": [35, 39],
  "egypt": [30, 27], "south africa": [24, -29], "nigeria": [8, 10], "kenya": [37, 1],
  "australia": [134, -25], "new zealand": [174, -41],
  "norway offshore": [3, 60], "north sea": [3, 58], "gulf of mexico": [-90, 26],
};

function coordsFor(name) {
  if (!name) return null;
  const key = name.trim().toLowerCase();
  if (COUNTRY_COORDS[key]) return COUNTRY_COORDS[key];
  // try last word
  const parts = key.split(/[, ]+/);
  for (const p of parts) if (COUNTRY_COORDS[p]) return COUNTRY_COORDS[p];
  return null;
}

function lonLatToXY(lon, lat, w = 1000, h = 500) {
  return { x: ((lon + 180) / 360) * w, y: ((90 - lat) / 180) * h };
}

// Normalize a pasted tracking number: strip whitespace/nbsp, convert unicode dashes to ASCII, uppercase.
function normalizeTn(v) {
  return (v || "")
    .replace(/[\u00A0\s]+/g, "")
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .trim()
    .toUpperCase();
}

export default function Track() {
  const { tn } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(tn || "");
  const [loading, setLoading] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (tn) {
      lookup(tn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tn]);

  const lookup = async (value) => {
    const v = normalizeTn(value);
    if (!v) return;
    setLoading(true);
    setError("");
    setShipment(null);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(v)}`);
      setShipment(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Tracking number not found");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const v = normalizeTn(input);
    if (!v) return;
    setInput(v);
    if (v === tn) {
      // Same URL — useEffect won't fire, run lookup manually
      lookup(v);
    } else {
      navigate(`/track/${v}`);
    }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20">
      <SectionLabel>Live Tracking</SectionLabel>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        <h1 className="lg:col-span-8 font-display font-black uppercase tracking-tight text-5xl sm:text-6xl text-[#F0F4F8] leading-[0.95]">
          Where is your <span className="text-[#D4AF37]">consignment?</span>
        </h1>
        <p className="lg:col-span-4 text-[#9EABC0]">
          Enter the tracking number issued to your operations desk. Every hand-off is signed and time-stamped by an armed courier.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        data-testid="track-form"
        className="mt-10 flex items-stretch max-w-2xl border border-[#A1AEC2]/30 bg-[#0A1024]"
      >
        <div className="px-4 flex items-center border-r border-[#A1AEC2]/20">
          <Search className="w-4 h-4 text-[#D4AF37]" />
        </div>
        <input
          data-testid="track-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="APF-2026-XXXXXX"
          className="flex-1 bg-transparent px-4 py-4 font-mono text-sm uppercase text-[#F0F4F8] placeholder-[#9EABC0]/60 outline-none tracking-wider"
        />
        <button
          type="submit"
          data-testid="track-submit"
          disabled={loading}
          className="bg-[#D4AF37] text-[#060C1F] font-bold uppercase tracking-widest text-xs px-8 hover:bg-[#C5A059] disabled:opacity-60 flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />} Track
        </button>
      </form>

      {error && (
        <div data-testid="track-error" className="mt-8 border border-red-800/60 bg-red-950/30 p-6 flex items-center gap-4 max-w-2xl">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-red-400">Lookup Failed</div>
            <div className="text-sm text-[#F0F4F8] mt-1">{error}</div>
          </div>
        </div>
      )}

      {shipment && <ShipmentView shipment={shipment} />}

      {!shipment && !error && !loading && (
        <div className="mt-16 border border-dashed border-[#A1AEC2]/25 p-10 max-w-2xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9EABC0]">Where to find your number</div>
          <div className="mt-4 text-sm text-[#F0F4F8]">
            Your relationship manager sends the tracking number in your intake email.<br />
            Format: <span className="font-mono text-[#D4AF37]">APF-2026-XXXXXX</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ShipmentView({ shipment }) {
  const events = [...(shipment.events || [])].reverse();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mt-14 space-y-6"
      data-testid="shipment-detail"
    >
      {/* Live Convoy map */}
      <ConvoyMap shipment={shipment} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 border border-[#A1AEC2]/20 bg-[#0A1024]">
          <div className="p-6 border-b border-[#A1AEC2]/15 flex flex-wrap justify-between items-start gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Tracking Number</div>
              <div className="mt-2 font-mono text-2xl text-[#F0F4F8]">{shipment.tracking_number}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">
                Client · {shipment.client_name}
              </div>
            </div>
            <StatusBadge status={shipment.status} />
          </div>

          <div className="p-6 grid grid-cols-3 gap-4">
            <RoutePoint label="Origin" country={shipment.origin_country} state="done" />
            <RoutePoint label="Current" country={shipment.current_country} sub={shipment.current_location} state="active" />
            <RoutePoint label="Destination" country={shipment.destination_country} state="pending" />
          </div>

          <div className="border-t border-[#A1AEC2]/15 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-6">Movement Timeline</div>
            <div className="space-y-6">
              {events.map((ev, i) => {
                const Icon = STATUS_ICONS[ev.status] || CircleDot;
                const isLatest = i === 0;
                return (
                  <div key={i} className="flex gap-6" data-testid={`event-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 border flex items-center justify-center ${isLatest ? "border-[#D4AF37] bg-[#D4AF37]/10 shadow-[0_0_15px_rgba(212,175,55,0.35)]" : "border-[#A1AEC2]/30 bg-[#060C1F]"}`}>
                        <Icon className={`w-4 h-4 ${isLatest ? "text-[#D4AF37]" : "text-[#9EABC0]"}`} />
                      </div>
                      {i < events.length - 1 && <div className={`w-px flex-1 min-h-8 ${isLatest ? "bg-[#D4AF37]/60" : "bg-[#A1AEC2]/25"}`} />}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <div>
                          <div className={`font-display font-bold ${isLatest ? "text-[#D4AF37]" : "text-[#F0F4F8]"}`}>
                            {ev.status}
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-sm text-[#F0F4F8]">
                            <MapPin className="w-3.5 h-3.5 text-[#9EABC0]" />
                            {ev.country}{ev.location ? ` · ${ev.location}` : ""}
                          </div>
                          {ev.notes && <div className="mt-2 text-sm text-[#9EABC0]">{ev.notes}</div>}
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0]">
                          {new Date(ev.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="border border-[#A1AEC2]/20 bg-[#0A1024] p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Manifest</div>
            <dl className="space-y-3 text-sm">
              <Row k="Cargo Type" v={shipment.cargo_type} />
              <Row k="Origin" v={shipment.origin_country} />
              <Row k="Destination" v={shipment.destination_country} />
              <Row k="Security Level" v={shipment.security_level} highlight />
              {shipment.estimated_value && <Row k="Est. Value" v={shipment.estimated_value} />}
              <Row k="Registered" v={new Date(shipment.created_at).toLocaleDateString()} />
              <Row k="Last Update" v={new Date(shipment.updated_at).toLocaleString()} />
            </dl>
          </div>

          <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6 relative overflow-hidden">
            <div className="absolute inset-0 aegis-scan pointer-events-none bg-gradient-to-b from-transparent via-[#D4AF37]/10 to-transparent" />
            <ShieldCheck className="w-6 h-6 text-[#D4AF37]" />
            <div className="mt-4 font-display font-bold text-[#F0F4F8]">Chain-of-Custody Verified</div>
            <p className="mt-2 text-xs text-[#9EABC0]">
              Cryptographic timestamp signed by A&amp;P Ops Node #{Math.floor(Math.random() * 47) + 1}. Auditable on request.
            </p>
          </div>
        </aside>
      </div>
    </motion.div>
  );
}

/**
 * Live convoy world-map: draws origin → current → destination and animates an
 * armored icon along the completed portion (origin → current) with an idle
 * bob at the current position, plus a dashed pending leg (current → destination).
 */
function ConvoyMap({ shipment }) {
  const originC = coordsFor(shipment.origin_country);
  const currentC = coordsFor(shipment.current_country) || originC;
  const destC = coordsFor(shipment.destination_country);

  const isReturned = shipment.status === "Returned to Sender";
  const isDelivered = ["Delivered", "Vaulted"].includes(shipment.status);
  const isSea = /offshore|sea|marine|ship/i.test(shipment.cargo_type || "") || /offshore|sea/i.test(shipment.current_location || "");
  const ConvoyIcon = isSea ? Ship : Truck;

  const w = 1000, h = 500;
  const A = originC ? lonLatToXY(originC[0], originC[1], w, h) : null;
  const B = currentC ? lonLatToXY(currentC[0], currentC[1], w, h) : null;
  const C = destC ? lonLatToXY(destC[0], destC[1], w, h) : null;

  // Build path A→B (completed) and B→C (pending)
  const completedPath = useMemo(() => {
    if (!A || !B) return null;
    const mx = (A.x + B.x) / 2;
    const my = (A.y + B.y) / 2 - Math.abs(A.x - B.x) * 0.14;
    return `M${A.x},${A.y} Q${mx},${my} ${B.x},${B.y}`;
  }, [A, B]);
  const pendingPath = useMemo(() => {
    if (!B || !C) return null;
    const mx = (B.x + C.x) / 2;
    const my = (B.y + C.y) / 2 - Math.abs(B.x - C.x) * 0.14;
    return `M${B.x},${B.y} Q${mx},${my} ${C.x},${C.y}`;
  }, [B, C]);

  if (!A && !B && !C) return null;

  return (
    <div className="relative border border-[#A1AEC2]/20 bg-[#0A1024] p-4" data-testid="convoy-map">
      <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
        <span className="w-2 h-2 rounded-full bg-[#D4AF37] aegis-pulse" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
          Live Convoy · {shipment.status}
        </span>
      </div>
      <div className="absolute top-4 right-4 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#9EABC0]">
        {shipment.origin_country} → {shipment.current_country} → {shipment.destination_country}
      </div>

      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
        <defs>
          <pattern id="convoy-dots" width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.7" fill="#9EABC0" opacity="0.16" />
          </pattern>
          <radialGradient id="convoy-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Simplified continents backdrop */}
        <g fill="url(#convoy-dots)" stroke="rgba(158,171,192,0.12)" strokeWidth="0.5">
          <path d="M120,110 Q160,80 220,90 T310,110 Q340,140 320,180 L280,220 Q240,240 200,230 L160,220 Q130,200 120,170 Z" />
          <path d="M260,240 Q290,260 300,300 Q310,360 280,410 Q260,430 240,410 Q220,380 230,340 Q240,290 260,240 Z" />
          <path d="M470,110 Q510,95 550,110 Q580,130 570,160 Q540,175 500,170 Q470,160 470,110 Z" />
          <path d="M490,190 Q540,180 570,210 Q590,260 580,320 Q560,380 530,400 Q500,380 490,340 Q470,290 490,190 Z" />
          <path d="M580,110 Q660,90 760,110 Q830,140 850,190 Q830,230 780,240 Q700,230 640,210 Q590,180 580,110 Z" />
          <path d="M660,220 Q700,230 730,260 Q740,290 710,310 Q680,300 660,270 Z" />
          <path d="M800,340 Q860,330 890,360 Q890,395 850,410 Q810,405 790,380 Z" />
        </g>

        {/* Completed leg */}
        {completedPath && (
          <path
            d={completedPath}
            fill="none"
            stroke="#D4AF37"
            strokeWidth="2"
            strokeLinecap="round"
            opacity="0.95"
          />
        )}
        {/* Pending leg */}
        {pendingPath && !isReturned && (
          <path
            d={pendingPath}
            fill="none"
            stroke="#D4AF37"
            strokeOpacity="0.55"
            strokeWidth="1.5"
            strokeDasharray="6 8"
            className="aegis-route"
          />
        )}

        {/* Origin marker */}
        {A && (
          <g>
            <circle cx={A.x} cy={A.y} r="10" fill="url(#convoy-glow)" />
            <circle cx={A.x} cy={A.y} r="4" fill="#F0F4F8" stroke="#D4AF37" strokeWidth="1" />
            <text x={A.x + 8} y={A.y - 8} fill="#9EABC0" fontSize="10" fontFamily="JetBrains Mono, monospace" style={{ letterSpacing: "0.1em" }}>
              {(shipment.origin_country || "").toUpperCase()}
            </text>
          </g>
        )}
        {/* Destination marker */}
        {C && (
          <g>
            <circle cx={C.x} cy={C.y} r="4" fill="#9EABC0" opacity={isDelivered ? 1 : 0.6} />
            <text x={C.x + 8} y={C.y - 8} fill="#9EABC0" fontSize="10" fontFamily="JetBrains Mono, monospace" style={{ letterSpacing: "0.1em" }}>
              {(shipment.destination_country || "").toUpperCase()}
            </text>
          </g>
        )}

        {/* Animated convoy along completed path */}
        {completedPath && (
          <g>
            <circle r="14" fill="url(#convoy-glow)">
              <animateMotion dur="4s" repeatCount="indefinite" path={completedPath} rotate="auto" />
            </circle>
            {/* SVG icon "sprite" using a foreignObject for lucide icon */}
            <g>
              <animateMotion dur="4s" repeatCount="indefinite" path={completedPath} rotate="0" />
              <rect x="-9" y="-7" width="18" height="14" fill="#060C1F" stroke="#D4AF37" strokeWidth="1.2" />
              <rect x="-6" y="-4" width="7" height="4" fill="#D4AF37" opacity="0.55" />
              <circle cx="-5" cy="7" r="2" fill="#D4AF37" />
              <circle cx="5" cy="7" r="2" fill="#D4AF37" />
            </g>
          </g>
        )}

        {/* Current position label (large gold dot at B) */}
        {B && (
          <g>
            <circle cx={B.x} cy={B.y} r="16" fill="url(#convoy-glow)" />
            <circle cx={B.x} cy={B.y} r="5" fill="#D4AF37" stroke="#F0F4F8" strokeWidth="1" />
            <circle cx={B.x} cy={B.y} r="5" fill="#D4AF37" className="aegis-pulse" opacity="0.7" />
          </g>
        )}
      </svg>

      <div className="mt-3 grid grid-cols-3 gap-3 border-t border-[#A1AEC2]/15 pt-3">
        <Leg icon={isSea ? Anchor : PackageCheck} label="Departed" value={shipment.origin_country} done />
        <Leg icon={ConvoyIcon} label={isDelivered ? "Arrived" : "En Route"} value={shipment.current_location || shipment.current_country} active />
        <Leg icon={ShieldCheck} label="Destination" value={shipment.destination_country} done={isDelivered} />
      </div>
    </div>
  );
}

function Leg({ icon: Icon, label, value, active, done }) {
  const color = active ? "text-[#D4AF37]" : done ? "text-[#F0F4F8]" : "text-[#9EABC0]";
  return (
    <div className="flex items-start gap-3">
      <Icon className={`w-4 h-4 mt-0.5 ${color}`} />
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9EABC0]">{label}</div>
        <div className={`text-xs mt-0.5 ${color}`}>{value || "—"}</div>
      </div>
    </div>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className="flex justify-between gap-3 py-1 border-b border-[#A1AEC2]/10 last:border-0">
      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EABC0] pt-0.5">{k}</dt>
      <dd className={`text-right ${highlight ? "text-[#D4AF37] font-semibold" : "text-[#F0F4F8]"}`}>{v}</dd>
    </div>
  );
}

function RoutePoint({ label, country, sub, state }) {
  const color = state === "done" ? "text-[#D4AF37]" : state === "active" ? "text-[#F0F4F8]" : "text-[#9EABC0]";
  const border = state === "active" ? "border-[#D4AF37]" : "border-[#A1AEC2]/30";
  return (
    <div className={`border ${border} p-4`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9EABC0]">{label}</div>
      <div className={`mt-2 font-display font-bold ${color}`}>{country}</div>
      {sub && <div className="text-xs text-[#9EABC0] mt-1">{sub}</div>}
      {state === "active" && (
        <div className="mt-2 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full aegis-pulse" />
          <span className="font-mono text-[9px] uppercase tracking-widest text-[#D4AF37]">Live</span>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const isDelivered = status === "Delivered" || status === "Vaulted";
  const isHold = ["On Hold", "Held by Customs", "Delayed", "Damaged", "Returned to Sender"].includes(status);
  const color = isDelivered
    ? "border-green-600 text-green-400 bg-green-900/20"
    : isHold ? "border-red-600 text-red-400 bg-red-900/20"
    : "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10";
  return (
    <div className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] ${color}`}>
      {status}
    </div>
  );
}
